// ============================================================================
// src/lib/db/dld-comparison.ts
// PV-1052 · PR-G — Wrapper RPC `get_deal_dld_comparison` consumed by BenchmarkPanel
// Refs: ADR-044 §D1 (4-level cascade), ADR-046 (config 3m/6m + display_stat),
//       supabase/migrations/20260526010100_get_deal_dld_comparison_v2.sql,
//       memory dld-matching-business-rules.
//
// PATTERN — mirrors `getAdminTopDeals` (src/lib/db/admin/kpi.ts) : pure async
// function, throws on Supabase error, no SWR / React Query wrapper. Consumed
// by the `useDealDldComparison` hook which handles the discriminated union
// state for the React layer.
//
// JSON SHAPE — 18 keys (see SQL migration v2 lines 405-424). `confidence` and
// `display_stat` are NULL only when scope='not_found' (early return payload
// is just `{ scope: 'not_found' }`). All other scopes surface display_stat.
// ============================================================================

import { getSupabaseClient, type DdmSupabaseClient } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Type unions — exhaustive over the SQL CASE branches in the RPC
// ---------------------------------------------------------------------------

// The windowed families carry the config-driven month count in the scope
// string itself (`same_building_<tight|wide>m`), so the window is NOT fixed to
// 3/6 — an admin can set any value via Settings M12. Use template-literal types
// so non-default windows (e.g. `same_building_4m`) stay in-contract instead of
// being treated as unknown scopes by the panel. ADR-055 cascade families:
// building → project group → community → broader_market (community ±1 BR) →
// zone → zone_broader (zone ±1 BR).
export type DldComparisonScope =
  | `same_building_${number}m`
  | `same_project_group_${number}m`
  | `same_community_${number}m`
  | 'broader_market'
  | `same_zone_${number}m`
  | 'zone_broader'
  | 'off_plan'
  | 'insufficient_data'
  | 'not_found';

// Confidence is SCOPE-AWARE (ADR-055, supersedes ADR-047): building
// high (>= min_tx) / medium (>= 3) / low; project group medium iff n>=5 +
// homogeneous; community medium iff n>=10 + homogeneous; broader/zone always
// low; off-plan → off_plan_reference. ADR-055 display rule: every POSITIVE
// discount is surfaced with its exact perimeter — confidence is a
// transparency label, no longer a display gate (`discountDisplayMode`).
export type DldComparisonConfidence =
  | 'high'
  | 'medium'
  | 'low'
  | 'insufficient'
  | 'off_plan_reference';

export type DldDisplayStat = 'median' | 'mean';

// Anti-leak guarantee from the SQL RPC: NO `transaction_number`, NO `id`.
export type DldComparableTransaction = {
  transaction_date: string;
  building: string | null;
  community: string;
  property_sub_type: string;
  bedrooms: number;
  area_sqft: number;
  sale_price_aed: number;
  price_per_sqft: number;
};

export type DldComparisonResponse = {
  scope: DldComparisonScope;
  confidence: DldComparisonConfidence | null;
  display_stat: DldDisplayStat | null;
  period_months: number | null;
  sample_size: number | null;
  deal_price_per_sqft: number | null;
  median_price_per_sqft: number | null;
  average_price_per_sqft: number | null;
  p25_price_per_sqft: number | null;
  p75_price_per_sqft: number | null;
  min_price_per_sqft: number | null;
  max_price_per_sqft: number | null;
  latest_transaction_date: string | null;
  reference_price: number | null;
  estimated_market_value: number | null;
  discount_aed: number | null;
  discount_percent: number | null;
  comparable_transactions: DldComparableTransaction[];
};

// ---------------------------------------------------------------------------
// RPC call
// ---------------------------------------------------------------------------

/**
 * Fetches the DLD market comparison for a given deal UUID. The RPC is
 * SECURITY DEFINER + GRANT EXECUTE TO anon (with court-circuit `not_found`
 * for non-public deals — anti-leak via guard inside the function).
 *
 * Throws on transport / SQL error; callers (the hook) should catch and
 * surface as `{ status: 'error' }` state.
 */
export async function getDealDldComparison(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DldComparisonResponse> {
  const { data, error } = await client.rpc('get_deal_dld_comparison', { p_deal_id: dealId });
  if (error) throw error;
  // The RPC returns JSONB; supabase-js types it as Json (recursive). Cast to
  // our typed shape — the SQL contract is enforced by the pgTAP suite.
  return data as unknown as DldComparisonResponse;
}
